import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo } from '@/mocks/about';
import { useJsonLd } from '@/hooks/useJsonLd';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 亚洲一级日本精选内容导航平台';
  }, []);

  useJsonLd({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        '@id': 'https://z3ratu1.cn/about',
        url: 'https://z3ratu1.cn/about',
        name: `关于${aboutInfo.brandName}`,
        description: aboutInfo.slogan,
        inLanguage: 'zh-CN',
        isPartOf: { '@id': 'https://z3ratu1.cn/#website' },
        about: { '@id': 'https://z3ratu1.cn/#organization' },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://z3ratu1.cn/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: '关于我们',
              item: 'https://z3ratu1.cn/about',
            },
          ],
        },
      },
      {
        '@type': 'Organization',
        '@id': 'https://z3ratu1.cn/#organization',
        name: aboutInfo.brandName,
        url: 'https://z3ratu1.cn/',
        slogan: aboutInfo.slogan,
        description: aboutInfo.introduction,
        email: aboutInfo.contact.email,
        telephone: aboutInfo.contact.phone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: aboutInfo.contact.address,
        },
        contactPoint: {
          '@type': 'ContactPoint',
          email: aboutInfo.contact.email,
          telephone: aboutInfo.contact.phone,
          contactType: 'customer service',
          availableLanguage: ['Chinese', 'Japanese'],
        },
      },
    ],
  });

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言！我们会尽快回复。');
      return;
    }
    setFormStatus('loading');
    try {
      const formData = new FormData(form);
      if (honeypot) formData.delete('company_alt');
      const response = await fetch('https://readdy.ai/api/form/d9u01378l97n3eas3eng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言！我们会尽快回复。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        setFormStatus('error');
        setFormMessage(serverMsg.includes('spam') || serverMsg.includes('form data is spam')
          ? '提交失败，请稍后再试。'
          : serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试。');
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-24 pb-14">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={[{ label: '关于我们' }]} />
          </div>

          {/* Hero */}
          <section className="mb-14 md:mb-20 text-center">
            <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mx-auto mb-5 rounded-2xl bg-primary-100 text-primary-600">
              <i className="ri-sun-line text-2xl md:text-3xl"></i>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-4">
              关于{aboutInfo.brandName}
            </h1>
            <p className="text-base md:text-lg text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              {aboutInfo.slogan}
            </p>
          </section>

          {/* Stats */}
          <section className="mb-14 md:mb-20">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {aboutInfo.stats.map((stat, idx) => (
                <div key={idx} className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
                  <p className="font-heading text-2xl md:text-3xl font-bold text-primary-600 mb-1">{stat.value}</p>
                  <p className="text-xs md:text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Brand introduction */}
          <section className="mb-14 md:mb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-start">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                  品牌介绍
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {aboutInfo.introduction}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {aboutInfo.mission}
                </p>
              </div>
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20Japanese%20office%20workspace%20with%20warm%20wooden%20furniture%2C%20large%20windows%20with%20natural%20light%2C%20traditional%20Japanese%20art%20on%20walls%2C%20clean%20and%20elegant%20aesthetic%2C%20professional%20editorial%20photography%2C%20warm%20neutral%20tones&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="亚洲一级日本品牌办公室"
                  title="亚洲一级日本精选内容导航平台 - 品牌办公室"
                  className="w-full h-64 md:h-80 object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="mb-14 md:mb-20">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {aboutInfo.values.map((val, idx) => (
                <div key={idx} className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                    <i className={`${['ri-shield-star-line', 'ri-heart-2-line', 'ri-user-star-line', 'ri-lightbulb-flash-line'][idx]} text-base`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-base text-foreground-900 mb-2">{val.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{val.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-14 md:mb-20">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
              荣誉资质
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {aboutInfo.reputation.map((rep, idx) => (
                <div key={idx} className="flex items-center gap-4 p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-award-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-900 mb-1">
                      {rep.title}
                    </h3>
                    <p className="text-xs text-foreground-500">
                      {rep.source} · {rep.year}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-14 md:mb-20">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-foreground-900 mb-1">联系电话</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-foreground-900 mb-1">办公地址</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-wechat-line"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-foreground-900 mb-1">微信公众号</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                <h3 className="font-heading font-semibold text-base text-foreground-900 mb-5">发送消息</h3>
                <form data-readdy-form onSubmit={handleContactSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="spam-catcher"
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg font-medium text-sm transition-all duration-200 disabled:opacity-60 whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送消息'}
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">{formMessage}</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formMessage}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}